import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import cookies from '@buzzfeed/bf-utils/lib/cookies';
import { SignInModal } from '@buzzfeed/react-components';
import { getUserInfo } from '@buzzfeed/bf-utils/lib/user';
import { useSignInTracking } from '../../hooks/analytics/useSignInTracking';
import {
  getBuzzBookmarkStatus,
  setBuzzBookmarkStatus
} from '../../api/upstreams/bookmarks-api-client';
import { BookmarkOutlinedIcon } from '../Svgs/BookmarkOutlinedIcon';
import styles from './bookmarkButton.module.scss';

const BOOKMARK_ACTION_ERROR_MESSAGE = 'Something went wrong. Try again.';

const BookmarkButton = ({ buzz }) => {
  const isLoginRef = useRef(false);
  const [signInModalIsOpen, setSignInModalIsOpen] = useState(false);
  const [isBookmarkSelected, setIsBookmarkSelected] = useState(false);
  const [isBookmarkPending, setIsBookmarkPending] = useState(false);
  const [bookmarkPopupMessage, setBookmarkPopupMessage] = useState('');
  const bookmarkPopupTimeoutRef = useRef(null);

  const bookmarkUnitTrackingData = useMemo(
    () => ({
      unit_type: 'buzz_bottom',
      unit_name: buzz.id,
      item_name: 'bookmark_sign_up',
      item_type: 'button',
      target_content_type: 'auth',
      target_content_id: 'sign_in',
    }),
    [buzz.id]
  );

  const trackBookmarkSignupClick = useSignInTracking(
    buzz,
    bookmarkUnitTrackingData
  );

  const loadBookmarks = useCallback(async () => {
    setIsBookmarkPending(true);
    try {
      const res = await getBuzzBookmarkStatus(buzz.id);
      if (res.status === 401) {
        isLoginRef.current = false;
        return;
      }
      setIsBookmarkSelected(res.ok);
    } finally {
      setIsBookmarkPending(false);
    }
  }, [buzz.id]);

  useEffect(() => {
    const userAuth = !!cookies.get('bf2-b_info');
    const userInfo = getUserInfo() || null;
    if (userInfo && userAuth) {
      isLoginRef.current = true;
      loadBookmarks();
    }
  }, [loadBookmarks]);


  useEffect(() => () => {
    if (bookmarkPopupTimeoutRef.current) {
      clearTimeout(bookmarkPopupTimeoutRef.current);
    }
  }, []);

  const showBookmarkPopup = (message) => {
    setBookmarkPopupMessage(message);

    if (bookmarkPopupTimeoutRef.current) {
      clearTimeout(bookmarkPopupTimeoutRef.current);
    }

    bookmarkPopupTimeoutRef.current = setTimeout(() => {
      setBookmarkPopupMessage('');
      bookmarkPopupTimeoutRef.current = null;
    }, 2000);
  };

  const handleBookmarkClick = useCallback(async () => {
    if (isBookmarkPending) {
      return;
    }

    if (!isLoginRef.current) {
      setSignInModalIsOpen(true);
      return;
    }

    setIsBookmarkPending(true);
    const action = isBookmarkSelected ? 'DELETE' : 'PUT';
    await setBuzzBookmarkStatus({buzzId: buzz.id, action})
      .then(({ ok, status}) => {
        if (status === 401) {
          isLoginRef.current = false;
          setSignInModalIsOpen(true);
          return;
        }
        if (ok) {
          setIsBookmarkSelected((prev) => {
            const message = prev ? 'Removed!' : 'Added!';
            showBookmarkPopup(message);
            return !prev;
          });
          return;
        }
        showBookmarkPopup(BOOKMARK_ACTION_ERROR_MESSAGE);
      }).finally(() => {
        setIsBookmarkPending(false);
      });

  }, [buzz.id, isBookmarkPending, isBookmarkSelected]);

  return (
    <div className={styles.bookmarkWrap}>
      {bookmarkPopupMessage ? (
        <div className={styles.bookmarkPopup} role="status" aria-live="polite">
          {bookmarkPopupMessage}
        </div>
      ) : null}
      <button
        type='button'
        className={`${styles.btnBookmark} ${isBookmarkSelected ? styles.btnBookmarkSelected : ''}`}
        onClick={handleBookmarkClick}
        aria-pressed={isBookmarkSelected}
        disabled={isBookmarkPending}
      >
        <BookmarkOutlinedIcon />
        <div className={styles.btnLabel}>
          {isBookmarkSelected
            ? ' Press to remove the post from your bookmarks '
            : ' Press to add the post to your bookmarks '}
        </div>
        {!isBookmarkSelected && (
          <span className={styles.btnText}>Save</span>
        )}
      </button>
      <SignInModal
        isOpen={signInModalIsOpen}
        onClose={() => setSignInModalIsOpen(false)}
        track={trackBookmarkSignupClick}
      />
    </div>
  );
};

export default BookmarkButton;
